/**
 * Print utility for ChartViewer component
 * Handles creating a print-optimized view of knitting charts
 */

export const printChart = (title, chartElement, keyElement) => {
  if (!chartElement) {
    console.warn('Chart content not found for printing');
    return;
  }

  // Create a new window for printing
  const printWindow = window.open('', '_blank');

  // Get all stylesheets from the current document
  const stylesheets = Array.from(document.styleSheets)
    .map(styleSheet => {
      try {
        return Array.from(styleSheet.cssRules)
          .map(rule => rule.cssText)
          .join('\n');
      } catch (e) {
        // Handle cross-origin stylesheets
        return '';
      }
    })
    .join('\n');

  // Create print HTML with all styles included
  const printHTML = `
    <!DOCTYPE html>
    <html>
      <head>
        <title>Knitting Chart - ${title}</title>
        <style>
          ${stylesheets}
          
          @media print {
            body { 
              margin: 0 !important; 
              padding: 20px !important; 
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
              background: white !important;
              overflow: hidden !important;
            }
            .print-title { 
              font-size: 18px !important; 
              font-weight: bold !important; 
              margin-bottom: 16px !important; 
              text-align: center !important; 
              color: black !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            .print-content { 
              display: flex !important; 
              gap: 8px !important; 
              align-items: flex-start !important;
              page-break-inside: avoid !important;
              width: auto !important;
              max-width: 100% !important;
              overflow: visible !important;
            }
            .print-chart { 
              flex: none !important; 
              overflow: visible !important;
            }
            .print-key { 
              flex: none !important;
              min-width: 200px !important; 
              max-width: 220px !important;
              overflow: visible !important;
            }
            .no-print { 
              display: none !important; 
            }
            
            /* Force modern font on ALL elements */
            *, *::before, *::after { 
              -webkit-print-color-adjust: exact !important; 
              color-adjust: exact !important; 
              print-color-adjust: exact !important;
              overflow: visible !important;
              box-sizing: border-box !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Ensure chart grid displays properly */
            table { 
              border-collapse: separate !important; 
              border-spacing: 0 !important; 
              width: auto !important;
              max-width: 100% !important;
              overflow: visible !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            td, th { 
              border: 1px solid #ccc !important; 
              padding: 0 !important; 
              margin: 0 !important; 
              overflow: visible !important;
              font-size: 10px !important;
              line-height: 1 !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Remove borders from stitch key table */
            .print-key table {
              border: none !important;
              border-collapse: collapse !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            .print-key td, .print-key th {
              border: none !important;
              padding: 2px 4px !important;
              font-size: 11px !important;
              line-height: 1.3 !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Fix column and row labels font size */
            .chart-header, .chart-row-label, .chart-col-label {
              font-size: 8px !important;
              font-weight: normal !important;
              line-height: 1 !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Fix any text in the chart - override ALL possible font declarations */
            .print-chart *, .print-chart *::before, .print-chart *::after {
              font-size: 10px !important;
              line-height: 1.2 !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Key panel font fixes - override ALL possible font declarations */
            .print-key *, .print-key *::before, .print-key *::after {
              font-size: 11px !important;
              line-height: 1.3 !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Hide any Material-UI overlay elements */
            .MuiPaper-root, .MuiBox-root { 
              box-shadow: none !important; 
              background: transparent !important; 
              overflow: visible !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Force font on specific Typography elements that might be present */
            p, span, div, h1, h2, h3, h4, h5, h6, label, text {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            /* Remove any scrollbars */
            ::-webkit-scrollbar {
              display: none !important;
            }
            
            /* Ensure everything fits on page */
            html, body {
              overflow: hidden !important;
              width: 100% !important;
              height: 100% !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
            
            .print-content > * {
              overflow: visible !important;
              max-height: none !important;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif !important;
            }
          }
          
          @media screen {
            body { 
              padding: 20px; 
              font-family: Arial, sans-serif; 
              background: white;
            }
            .print-content { 
              display: flex; 
              gap: 8px; 
              align-items: flex-start; 
            }
            .print-chart { 
              flex: none; 
            }
            .print-key { 
              flex: none;
              min-width: 200px; 
            }
          }
        </style>
      </head>
      <body>
        <div class="print-title">${title}</div>
        <div class="print-content">
          <div class="print-chart">${chartElement.outerHTML}</div>
          ${keyElement ? `<div class="print-key">${keyElement.outerHTML}</div>` : ''}
        </div>
        <script>
          window.onload = function() {
            // Small delay to ensure styles are applied
            setTimeout(function() {
              window.print();
              window.onafterprint = function() {
                window.close();
              };
            }, 100);
          };
        </script>
      </body>
    </html>
  `;

  printWindow.document.write(printHTML);
  printWindow.document.close();
};

/**
 * Helper function to create a print handler for ChartViewer
 * @param {string} title - Chart title
 * @returns {function} Print handler function
 */
export const createPrintHandler = (title) => {
  return () => {
    // Get the chart and key content
    const chartElement = document.querySelector('[data-chart-print-content]');
    const keyElement = document.querySelector('[data-key-print-content]');
    
    printChart(title, chartElement, keyElement);
  };
};
